import { Node, NodeConfig } from 'konva/lib/Node';
import KonvaService from '.';
import { Layer } from 'konva/lib/Layer';
import { Line } from 'konva/lib/shapes/Line';

interface LineGuideStopItem {
  guide: number;
  snap: string;
  node: KonvaService | Node;
}
interface LineGuideStops {
  vertical: LineGuideStopItem[];
  horizontal: LineGuideStopItem[];
}

interface SnappingEdge {
  guide: number;
  offset: number;
  snap: string;
}
interface SnappingEdges {
  vertical: SnappingEdge[];
  horizontal: SnappingEdge[];
}

export class KonvaGuideline {
  private guidelineOffset = 5;
  private guidelineColor = 'rgb(0, 161, 255)';

  private konvaService: KonvaService;

  constructor(konvaService: KonvaService) {
    this.konvaService = konvaService;
  }

  private getLineGuideStops(konvaNodes: Node[]): LineGuideStops {
    const { width, height, scale } = this.konvaService.watchfaceCanvas;
    // we can snap to stage borders and the center of the stage
    const offsetX = this.konvaService.watchfaceCanvas.offsetX;
    const offsetY = this.konvaService.watchfaceCanvas.offsetY;
    const vertical: LineGuideStopItem[] = [
      { guide: offsetX, snap: 'start', node: this.konvaService },
      {
        guide: (width / 2) * scale + offsetX,
        snap: 'center',
        node: this.konvaService
      },
      { guide: width * scale + offsetX, snap: 'end', node: this.konvaService }
    ];
    const horizontal: LineGuideStopItem[] = [
      { guide: offsetY, snap: 'start', node: this.konvaService },
      {
        guide: (height / 2) * scale + offsetY,
        snap: 'center',
        node: this.konvaService
      },
      { guide: height * scale + offsetY, snap: 'end', node: this.konvaService }
    ];

    konvaNodes.forEach((node) => {
      const box = node.getClientRect();
      // console.log(box);

      // and we can snap to all edges of shapes
      vertical.push(
        ...[
          {
            guide: box.x,
            snap: 'start',
            node
          },
          { guide: box.x + box.width / 2, snap: 'center', node },
          { guide: box.x + box.width, snap: 'end', node }
        ]
      );
      horizontal.push(
        ...[
          {
            guide: box.y,
            snap: 'start',
            node
          },
          { guide: box.y + box.height / 2, snap: 'center', node },
          { guide: box.y + box.height, snap: 'end', node }
        ]
      );
    });
    return {
      vertical: vertical.flat(),
      horizontal: horizontal.flat()
    };
  }

  private getObjectSnappingEdges(node: Node): SnappingEdges {
    const box = node.getClientRect();
    const absPos = node.absolutePosition();

    return {
      vertical: [
        {
          guide: Math.round(box.x),
          offset: Math.round(absPos.x - box.x),
          snap: 'start'
        },
        {
          guide: Math.round(box.x + box.width / 2),
          offset: Math.round(absPos.x - box.x - box.width / 2),
          snap: 'center'
        },
        {
          guide: Math.round(box.x + box.width),
          offset: Math.round(absPos.x - box.x - box.width),
          snap: 'end'
        }
      ],
      horizontal: [
        {
          guide: Math.round(box.y),
          offset: Math.round(absPos.y - box.y),
          snap: 'start'
        },
        {
          guide: Math.round(box.y + box.height / 2),
          offset: Math.round(absPos.y - box.y - box.height / 2),
          snap: 'center'
        },
        {
          guide: Math.round(box.y + box.height),
          offset: Math.round(absPos.y - box.y - box.height),
          snap: 'end'
        }
      ]
    };
  }

  // find all snapping possibilities
  private getGuides(lineGuideStops: LineGuideStops, itemBounds: SnappingEdges) {
    const resultV: {
      lineGuide: number;
      diff: number;
      snap: string;
      offset: number;
      node: KonvaService | Node;
    }[] = [];
    const resultH: {
      lineGuide: number;
      diff: number;
      snap: string;
      offset: number;
      node: KonvaService | Node;
    }[] = [];

    lineGuideStops.vertical.forEach((lineGuide: LineGuideStopItem) => {
      itemBounds.vertical.forEach(
        (itemBound: { guide: number; snap: string; offset: number }) => {
          const diff = Math.abs(lineGuide.guide - itemBound.guide);
          // if the distance between guild line and object snap point is close we can consider this for snapping
          if (diff < this.guidelineOffset) {
            resultV.push({
              lineGuide: lineGuide.guide,
              diff: diff,
              snap: itemBound.snap,
              offset: itemBound.offset,
              node: lineGuide.node
            });
          }
        }
      );
    });

    lineGuideStops.horizontal.forEach((lineGuide: LineGuideStopItem) => {
      itemBounds.horizontal.forEach(
        (itemBound: { guide: number; snap: string; offset: number }) => {
          const diff = Math.abs(lineGuide.guide - itemBound.guide);
          if (diff < this.guidelineOffset) {
            resultH.push({
              lineGuide: lineGuide.guide,
              diff: diff,
              snap: itemBound.snap,
              offset: itemBound.offset,
              node: lineGuide.node
            });
          }
        }
      );
    });

    const guides = [];

    // find closest snap
    const minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    const minH = resultH.sort((a, b) => a.diff - b.diff)[0];
    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: 'V',
        snap: minV.snap,
        node: minV.node
      });
    }
    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: 'H',
        snap: minH.snap,
        node: minH.node
      });
    }
    return guides;
  }

  private clear() {
    // clear all previous lines on the screen
    this.konvaService.stage.find('.guide-line').forEach((l) => l.destroy());
  }

  private getDrawStartAndEndPosition(
    node1: KonvaService | Node,
    node2: Node,
    snap: string,
    type: string
  ) {
    const node1Rect =
      node1 instanceof KonvaService
        ? {
            x: node1.watchfaceCanvas.offsetX,
            y: node1.watchfaceCanvas.offsetY,
            width: node1.watchfaceCanvas.width,
            height: node1.watchfaceCanvas.height
          }
        : node1.getClientRect();
    const node2Rect = node2.getClientRect();

    const [node1Start, node1Length, node2Start, node2Length] =
      type === 'V'
        ? [node1Rect.y, node1Rect.height, node2Rect.y, node2Rect.height]
        : [node1Rect.x, node1Rect.width, node2Rect.x, node2Rect.width];

    if (snap === 'center') {
      return [
        Math.min(node1Start, node2Start),
        Math.max(node1Start + node1Length, node2Start + node2Length)
      ];
    } else if (snap === 'start') {
      return [
        Math.min(node1Start, node2Start),
        Math.max(node1Start + node1Length, node2Start + node2Length)
      ];
    } else if (snap === 'end') {
      return [
        Math.min(node1Start, node2Start),
        Math.max(node1Start + node1Length, node2Start + node2Length)
      ];
    } else {
      return [0, 0];
    }
  }
  private _drawGuides(
    layer: Layer,
    guides: {
      lineGuide: number;
      offset: number;
      orientation: string;
      snap: string;
      node: KonvaService | Node;
    }[],
    node: Node
  ) {
    guides.forEach((lg) => {
      const { orientation, snap, node: lgNode } = lg;

      const [start, end] = this.getDrawStartAndEndPosition(
        lgNode,
        node,
        snap,
        orientation
      );
      if (orientation === 'H') {
        const line = new Line({
          points: [start, 0, end, 0],
          stroke: this.guidelineColor,
          strokeWidth: 1,
          name: 'guide-line',
          dash: [4, 6]
        });
        layer.add(line);
        line.absolutePosition({
          x: 0,
          y: lg.lineGuide
        });
      } else if (orientation === 'V') {
        const line = new Line({
          points: [0, start, 0, end],
          stroke: this.guidelineColor,
          strokeWidth: 1,
          name: 'guide-line',
          dash: [4, 6]
        });
        layer.add(line);
        line.absolutePosition({
          x: lg.lineGuide,
          y: 0
        });
      }
    });
  }

  getKonvaNodes(node: Node) {
    const ignoreNodes: Node[] = [node];
    const _find = (node: Node) => {
      node.getAttr('data')?.children?.forEach((node: Node<NodeConfig>) => {
        ignoreNodes.push(node);
        _find(node);
      });
    };
    _find(node);

    return (
      this.konvaService.stage
        .find('#shape')
        // 过滤元素
        .filter((node) => {
          return !ignoreNodes.includes(node);
        })
        .filter((node) => {
          return !!node.visible();
        })
    );
  }
  /**
   * 绘制某个图形的辅助线
   * @param node  绘制哪个节点的辅助线
   * @param layer 需要绘制在哪个图层上
   */
  drawGuides(node: Node, layer: Layer) {
    this.clear();

    const konvaNodes = this.getKonvaNodes(node);

    // find possible snapping lines
    const lineGuideStops = this.getLineGuideStops(konvaNodes);

    // find snapping points of current object
    const itemBounds = this.getObjectSnappingEdges(node);

    // now find where can we snap current object
    const guides = this.getGuides(lineGuideStops, itemBounds);

    // do nothing of no snapping
    if (!guides.length) {
      return;
    }

    this._drawGuides(layer, guides, node);

    // 吸附功能
    const absPos = node.absolutePosition();
    // now force object position
    guides.forEach((lg) => {
      switch (lg.snap) {
        case 'start': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
        case 'center': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
        case 'end': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
      }
    });
    node.absolutePosition(absPos);
  }
}
